import React, { useState } from 'react';
import { contactInfo } from '../../data/mock';
import { Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      // ⚠️ PLACEHOLDER: Cseréld ki a Formspree endpoint-ot a mock.js fájlban!
      const response = await fetch(contactInfo.formspreeEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="kapcsolat" className="py-24 bg-soft-gray">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="font-montserrat text-sm uppercase tracking-[0.2em] text-warm-sage mb-4">
            Beszéljünk!
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl text-charcoal">
            Kapcsolat
          </h2>
          <p className="font-montserrat text-base text-warm-gray mt-4 max-w-lg mx-auto">
            Írj, és 24–48 órán belül válaszolunk.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="font-playfair text-xl text-charcoal mb-6">
                  Közvetlen elérhetőségek
                </h3>
                
                {/* Phone */}
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow group mb-4"
                >
                  <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center group-hover:bg-warm-sage/20 transition-colors">
                    <Phone className="w-5 h-5 text-warm-sage" />
                  </div>
                  <div>
                    <p className="font-montserrat text-xs uppercase tracking-wider text-warm-gray mb-1">
                      Telefon
                    </p>
                    <p className="font-montserrat text-base text-charcoal font-medium">
                      {contactInfo.phone}
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow group"
                >
                  <div className="w-12 h-12 rounded-full bg-warm-sage/10 flex items-center justify-center group-hover:bg-warm-sage/20 transition-colors">
                    <Mail className="w-5 h-5 text-warm-sage" />
                  </div>
                  <div>
                    <p className="font-montserrat text-xs uppercase tracking-wider text-warm-gray mb-1">
                      Email
                    </p>
                    <p className="font-montserrat text-base text-charcoal font-medium">
                      {contactInfo.email}
                    </p>
                  </div>
                </a>
              </div>

              {/* Additional Info */}
              <div className="bg-warm-sage/10 rounded-lg p-6">
                <p className="font-montserrat text-sm text-charcoal leading-relaxed">
                  <strong>Időpont egyeztetés:</strong> Személyes vagy online konzultációra egyeztetünk időpontot, 
                  ahol részletesen átbeszjüljük elképzeléseidet és a lehetőségeket.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="font-playfair text-xl text-charcoal mb-6">
                Küldj üzenetet
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="font-montserrat text-sm text-warm-gray mb-2 block">
                    Név *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="A neved"
                    className="font-montserrat bg-soft-gray border-0 focus:ring-2 focus:ring-warm-sage/30"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="font-montserrat text-sm text-warm-gray mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="email@pelda.hu"
                    className="font-montserrat bg-soft-gray border-0 focus:ring-2 focus:ring-warm-sage/30"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="font-montserrat text-sm text-warm-gray mb-2 block">
                    Üzenet *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Miben segíthetünk? Mesélj az elképzeléseidről..."
                    rows={5}
                    className="font-montserrat bg-soft-gray border-0 focus:ring-2 focus:ring-warm-sage/30 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-warm-sage hover:bg-warm-sage/90 text-white font-montserrat text-sm uppercase tracking-wider py-6 transition-all duration-300 disabled:opacity-70"
                >
                  {status === 'submitting' ? (
                    'Küldés...'
                  ) : (
                    <>
                      Üzenet küldése
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                {/* Status Messages */}
                {status === 'success' && (
                  <div className="flex items-center gap-2 text-green-600 font-montserrat text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Köszönjük! Hamarosan jelentkezünk.
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-500 font-montserrat text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Hiba történt. Próbáld újra, vagy írj emailt!
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
